import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import { mediaUrl } from '../utils/mediaUrl';
import { League, Team } from '../types';

// ── Types ──────────────────────────────────────────────────────────────────────
interface StandingRow {
  team: Team; w: number; l: number; d: number; gf: number; ga: number; pts: number; gp: number;
}

interface PortalGame {
  id: string; title: string; sport: string; home_team: string; away_team: string;
  scheduled_at: string; status: string; stream_key?: string;
  home_final_score?: number; away_final_score?: number; team_id?: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmtDate = (s: string) => new Date(s).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

// ── CreateTeamModal ────────────────────────────────────────────────────────────
const CreateTeamModal = ({ leagueId, leagueSport, leagueSeasonYear, onClose, onCreated }: {
  leagueId: string; leagueSport: string; leagueSeasonYear: number;
  onClose: () => void; onCreated: (t: Team) => void;
}) => {
  const [form, setForm] = useState({
    name: '', sport: leagueSport, season_year: String(leagueSeasonYear),
    primary_color: '#22C55E', secondary_color: '#ffffff', description: '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const up = (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name.trim()) { setErr('Team name is required.'); return; }
    setSaving(true); setErr(null);
    try {
      const { data } = await api.post(`/leagues/${leagueId}/teams`, { ...form, season_year: parseInt(form.season_year) });
      onCreated(data);
    } catch (e: any) { setErr(e.response?.data?.error || 'Failed to create team.'); }
    finally { setSaving(false); }
  };

  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: '8px', fontSize: '14px', background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', color: 'var(--ink-1)', boxSizing: 'border-box' };
  const lbl: React.CSSProperties = { fontSize: '11px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '5px' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 800, margin: 0 }}>+ New Team</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--ink-3)' }}>×</button>
        </div>
        <div style={{ marginBottom: '14px' }}><div style={lbl}>Team Name *</div><input style={inp} value={form.name} onChange={up('name')} placeholder="e.g. Carmel FC U12" /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
          <div><div style={lbl}>Sport</div>
            <select style={inp} value={form.sport} onChange={up('sport')}>
              <option value="soccer">⚽ Soccer</option>
              <option value="baseball">⚾ Baseball</option>
              <option value="softball">🥎 Softball</option>
              <option value="basketball">🏀 Basketball</option>
            </select>
          </div>
          <div><div style={lbl}>Season Year</div><input style={inp} type="number" value={form.season_year} onChange={up('season_year')} /></div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
          <div><div style={lbl}>Primary Color</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input type="color" value={form.primary_color} onChange={up('primary_color')} style={{ width: 36, height: 36, border: 'none', borderRadius: '6px', cursor: 'pointer', padding: 2 }} />
              <input style={{ ...inp, flex: 1 }} value={form.primary_color} onChange={up('primary_color')} />
            </div>
          </div>
          <div><div style={lbl}>Secondary Color</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input type="color" value={form.secondary_color} onChange={up('secondary_color')} style={{ width: 36, height: 36, border: 'none', borderRadius: '6px', cursor: 'pointer', padding: 2 }} />
              <input style={{ ...inp, flex: 1 }} value={form.secondary_color} onChange={up('secondary_color')} />
            </div>
          </div>
        </div>
        <div style={{ marginBottom: '14px' }}><div style={lbl}>Description</div><textarea style={{ ...inp, minHeight: '60px', resize: 'vertical' } as React.CSSProperties} value={form.description} onChange={up('description')} placeholder="Optional description…" /></div>
        {err && <div style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: '8px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)', fontSize: '13px', color: '#F87171' }}>{err}</div>}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Creating…' : 'Create Team'}</button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ── CreateGameModal ────────────────────────────────────────────────────────────
const CreateGameModal = ({ leagueId, teams, onClose, onCreated }: {
  leagueId: string; teams: Team[]; onClose: () => void; onCreated: () => void;
}) => {
  const [form, setForm] = useState({
    title: '', sport: teams[0]?.sport || 'soccer',
    home_team: '', away_team: '', team_id: teams[0]?.id || '',
    scheduled_at: '', location: '', description: '', status: 'scheduled',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const up = (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [k]: e.target.value }));

  // Auto-generate title when teams change
  const autoTitle = () => {
    if (form.home_team && form.away_team) {
      setForm(f => ({ ...f, title: `${f.away_team} @ ${f.home_team}` }));
    }
  };

  const save = async () => {
    if (!form.title || !form.home_team || !form.away_team || !form.scheduled_at) {
      setErr('Title, teams, and date are required.'); return;
    }
    setSaving(true); setErr(null);
    try {
      await api.post(`/leagues/${leagueId}/games`, form);
      onCreated();
    } catch (e: any) { setErr(e.response?.data?.error || 'Failed to create game.'); }
    finally { setSaving(false); }
  };

  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: '8px', fontSize: '14px', background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', color: 'var(--ink-1)', boxSizing: 'border-box' };
  const lbl: React.CSSProperties = { fontSize: '11px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '5px' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 800, margin: 0 }}>+ Schedule Game</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--ink-3)' }}>×</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
          <div><div style={lbl}>Home Team</div>
            <input style={inp} value={form.home_team} placeholder="Home team name"
              onChange={up('home_team')} onBlur={autoTitle} />
          </div>
          <div><div style={lbl}>Away Team</div>
            <input style={inp} value={form.away_team} placeholder="Away team name"
              onChange={up('away_team')} onBlur={autoTitle} />
          </div>
        </div>
        <div style={{ marginBottom: '14px' }}><div style={lbl}>Game Title</div><input style={inp} value={form.title} onChange={up('title')} placeholder="e.g. Away @ Home" /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
          <div><div style={lbl}>Date & Time</div><input style={inp} type="datetime-local" value={form.scheduled_at} onChange={up('scheduled_at')} /></div>
          <div><div style={lbl}>Home Team Page</div>
            <select style={inp} value={form.team_id} onChange={up('team_id')}>
              <option value="">— Select team page —</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
          <div><div style={lbl}>Location</div><input style={inp} value={form.location} onChange={up('location')} placeholder="Field / venue name" /></div>
          <div><div style={lbl}>Sport</div>
            <select style={inp} value={form.sport} onChange={up('sport')}>
              <option value="soccer">⚽ Soccer</option>
              <option value="baseball">⚾ Baseball</option>
              <option value="softball">🥎 Softball</option>
              <option value="basketball">🏀 Basketball</option>
            </select>
          </div>
        </div>
        {err && <div style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: '8px', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)', fontSize: '13px', color: '#F87171' }}>{err}</div>}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Create Game'}</button>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

// ── Main LeaguePortal ──────────────────────────────────────────────────────────
export default function LeaguePortal() {
  const { isAdmin, isLeagueManager, managedLeagueIds } = useAuth();
  const navigate = useNavigate();

  const [leagues, setLeagues]         = useState<League[]>([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [league, setLeague]           = useState<League | null>(null);
  const [teams, setTeams]             = useState<Team[]>([]);
  const [games, setGames]             = useState<PortalGame[]>([]);
  const [standings, setStandings]     = useState<StandingRow[]>([]);
  const [loading, setLoading]         = useState(true);
  const [tab, setTab]                 = useState<'dashboard' | 'teams' | 'schedule' | 'standings' | 'settings'>('dashboard');
  const [showCreateTeam, setShowCreateTeam]   = useState(false);
  const [showCreateGame, setShowCreateGame]   = useState(false);
  const [settingsForm, setSettingsForm] = useState<Partial<League>>({});
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState<string | null>(null);
  const logoRef = useRef<HTMLInputElement>(null);

  // Load list of leagues the user manages
  const loadLeagues = useCallback(async () => {
    try {
      const r = await api.get('/leagues/my');
      setLeagues(r.data);
      if (r.data.length > 0 && !selectedLeagueId) {
        setSelectedLeagueId(r.data[0].id);
      }
    } catch {}
  }, [selectedLeagueId]);

  useEffect(() => { loadLeagues(); }, [loadLeagues]);

  // Load selected league data
  const loadLeagueData = useCallback(async () => {
    if (!selectedLeagueId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [lRes, tRes, gRes, sRes] = await Promise.all([
        api.get(`/leagues/${selectedLeagueId}`),
        api.get(`/leagues/${selectedLeagueId}/teams`),
        api.get(`/leagues/${selectedLeagueId}/games`),
        api.get(`/leagues/${selectedLeagueId}/standings`),
      ]);
      setLeague(lRes.data);
      setTeams(tRes.data);
      setGames(gRes.data);
      setStandings(sRes.data);
      setSettingsForm(lRes.data);
    } catch {} finally { setLoading(false); }
  }, [selectedLeagueId]);

  useEffect(() => { loadLeagueData(); }, [loadLeagueData]);

  const saveSettings = async () => {
    if (!league) return;
    setSettingsSaving(true); setSettingsMsg(null);
    try {
      await api.put(`/leagues/${league.id}`, settingsForm);
      setSettingsMsg('Saved!');
      await loadLeagueData();
    } catch (e: any) { setSettingsMsg(e.response?.data?.error || 'Save failed.'); }
    finally { setSettingsSaving(false); }
  };

  const uploadLogo = async (file: File) => {
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const { data } = await api.post('/videos/upload-photo', fd);
      setSettingsForm(f => ({ ...f, logo_url: data.photo_url }));
    } catch { alert('Logo upload failed.'); }
  };

  if (!isAdmin && !isLeagueManager) {
    return <div className="container" style={{ paddingTop: 40, textAlign: 'center', color: 'var(--ink-3)' }}>Access denied.</div>;
  }

  const upcoming = games.filter(g => g.status !== 'completed').sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime());
  const past     = games.filter(g => g.status === 'completed').sort((a, b) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime());

  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: '8px', fontSize: '14px', background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', color: 'var(--ink-1)', boxSizing: 'border-box' };
  const lbl: React.CSSProperties = { fontSize: '11px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '5px' };
  const card: React.CSSProperties = { background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: '12px', padding: '16px 20px' };

  const SPORT_EMOJI: Record<string, string> = { soccer: '⚽', baseball: '⚾', softball: '🥎', basketball: '🏀' };

  const renderGame = (g: PortalGame) => (
    <div key={g.id} style={{ ...card, display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', marginBottom: '8px' }}>
      <div style={{ fontSize: '22px', flexShrink: 0 }}>{SPORT_EMOJI[g.sport] || '🏆'}</div>
      <div style={{ flex: 1, minWidth: '160px' }}>
        <div style={{ fontWeight: 800, fontSize: '14px', color: 'var(--ink-1)', marginBottom: '2px' }}>{g.title}</div>
        <div style={{ fontSize: '12px', color: 'var(--ink-3)' }}>
          {g.away_team} @ {g.home_team}
          {g.home_final_score != null && g.away_final_score != null
            ? <span style={{ fontWeight: 900, color: 'var(--ink-1)', marginLeft: '8px' }}>{g.away_final_score}–{g.home_final_score}</span>
            : ''}
        </div>
        <div style={{ fontSize: '11px', color: 'var(--ink-4)', marginTop: '2px' }}>{fmtDate(g.scheduled_at)}</div>
      </div>
      <span style={{
        fontSize: '10px', fontWeight: 800, padding: '3px 9px', borderRadius: '20px', textTransform: 'uppercase', letterSpacing: '0.7px',
        background: g.status === 'live' ? 'rgba(244,67,54,0.15)' : g.status === 'completed' ? 'rgba(34,197,94,0.12)' : 'rgba(249,115,22,0.12)',
        color:      g.status === 'live' ? '#F44336'              : g.status === 'completed' ? '#22C55E'              : '#F97316',
        border:     g.status === 'live' ? '1px solid rgba(244,67,54,0.3)' : g.status === 'completed' ? '1px solid rgba(34,197,94,0.3)' : '1px solid rgba(249,115,22,0.3)',
      }}>{g.status === 'live' ? '🔴 LIVE' : g.status}</span>
      {g.id && <Link to={`/watch/${g.id}`} style={{ fontSize: '12px', fontWeight: 700, color: '#3B82F6', textDecoration: 'none' }}>Watch →</Link>}
    </div>
  );

  return (
    <div className="container" style={{ paddingTop: 8 }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '26px', fontWeight: 900, color: 'var(--ink-1)', marginBottom: '4px' }}>
            League Portal
          </h1>
          <p style={{ color: 'var(--ink-3)', fontSize: '14px' }}>Manage your league, teams, schedule, and standings.</p>
        </div>
        {/* League selector if managing multiple */}
        {leagues.length > 1 && (
          <select value={selectedLeagueId} onChange={e => setSelectedLeagueId(e.target.value)}
            style={{ padding: '8px 14px', borderRadius: '10px', fontSize: '14px', fontWeight: 700, background: 'var(--surface)', border: '1px solid var(--hairline)', color: 'var(--ink-1)', cursor: 'pointer' }}>
            {leagues.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--ink-4)' }}>Loading…</div>
      ) : !league ? (
        <div style={{ ...card, textAlign: 'center', padding: '60px' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏟️</div>
          <p style={{ color: 'var(--ink-3)', fontSize: '15px' }}>No league assigned to your account yet. Contact the admin.</p>
        </div>
      ) : (
        <>
          {/* ── League hero ── */}
          <div style={{ ...card, marginBottom: '20px', background: `linear-gradient(135deg, ${league.primary_color}18, ${league.secondary_color}10)`, border: `1px solid ${league.primary_color}30` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ width: 56, height: 56, borderRadius: '12px', background: `${league.primary_color}25`, border: `2px solid ${league.primary_color}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', flexShrink: 0 }}>
                {league.logo_url ? <img src={mediaUrl(league.logo_url)} alt={league.name} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '10px' }} /> : (SPORT_EMOJI[league.sport] || '🏟️')}
              </div>
              <div>
                <div style={{ fontSize: '20px', fontWeight: 900, color: 'var(--ink-1)' }}>{league.name}</div>
                <div style={{ fontSize: '13px', color: 'var(--ink-3)', marginTop: '2px' }}>
                  {SPORT_EMOJI[league.sport]} {league.sport} · {league.season_year}
                  {league.location ? ` · ${league.location}` : ''}
                </div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                {[
                  { v: teams.length,   l: 'Teams' },
                  { v: games.length,   l: 'Games' },
                  { v: upcoming.length,l: 'Upcoming' },
                ].map(s => (
                  <div key={s.l} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '22px', fontWeight: 900, color: league.primary_color }}>{s.v}</div>
                    <div style={{ fontSize: '10px', color: 'var(--ink-4)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Tabs ── */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid var(--hairline)', paddingBottom: '4px', flexWrap: 'wrap' }}>
            {([
              { id: 'dashboard', label: '📊 Dashboard' },
              { id: 'teams',    label: `🏆 Teams (${teams.length})` },
              { id: 'schedule', label: `📅 Schedule (${games.length})` },
              { id: 'standings',label: '🏅 Standings' },
              { id: 'settings', label: '⚙️ Settings' },
            ] as { id: typeof tab; label: string }[]).map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                padding: '8px 16px', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                background: tab === t.id ? `${league.primary_color}20` : 'transparent',
                color: tab === t.id ? league.primary_color : 'var(--ink-3)',
              }}>{t.label}</button>
            ))}
          </div>

          {/* ── Dashboard tab ── */}
          {tab === 'dashboard' && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                {[
                  { label: 'Teams',    value: teams.length,                                           color: league.primary_color },
                  { label: 'Total Games', value: games.length,                                        color: '#3B82F6' },
                  { label: 'Upcoming', value: upcoming.length,                                        color: '#F97316' },
                  { label: 'Completed',value: games.filter(g => g.status === 'completed').length,     color: '#22C55E' },
                ].map(s => (
                  <div key={s.label} style={{ ...card, textAlign: 'center' }}>
                    <div style={{ fontSize: '32px', fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</div>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px' }}>{s.label}</div>
                  </div>
                ))}
              </div>
              {upcoming.length > 0 && (
                <>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>Next Up</div>
                  {upcoming.slice(0, 3).map(renderGame)}
                  {upcoming.length > 3 && <div style={{ textAlign: 'center', marginTop: '8px' }}><button className="btn btn-ghost btn-sm" onClick={() => setTab('schedule')}>View all →</button></div>}
                </>
              )}
            </div>
          )}

          {/* ── Teams tab ── */}
          {tab === 'teams' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <button className="btn btn-primary" onClick={() => setShowCreateTeam(true)}>+ New Team</button>
              </div>
              {teams.length === 0 ? (
                <div style={{ ...card, textAlign: 'center', padding: '52px 20px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>🏆</div>
                  <p style={{ color: 'var(--ink-3)', marginBottom: '16px' }}>No teams yet. Create the first one.</p>
                  <button className="btn btn-primary" onClick={() => setShowCreateTeam(true)}>+ Create Team</button>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
                  {teams.map(t => (
                    <Link key={t.id} to={`/teams/${t.id}`} style={{ textDecoration: 'none' }}>
                      <div style={{ ...card, cursor: 'pointer' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ width: 42, height: 42, borderRadius: '10px', background: `${t.primary_color}20`, border: `2px solid ${t.primary_color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>
                            {t.logo_url ? <img src={mediaUrl(t.logo_url)} alt={t.name} style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '8px' }} /> : SPORT_EMOJI[t.sport] || '🏆'}
                          </div>
                          <div>
                            <div style={{ fontWeight: 800, fontSize: '14px', color: 'var(--ink-1)' }}>{t.name}</div>
                            <div style={{ fontSize: '12px', color: 'var(--ink-3)', marginTop: '2px' }}>{t.sport} · {t.season_year}</div>
                          </div>
                          <div style={{ marginLeft: 'auto', fontSize: '13px', color: 'var(--ink-4)' }}>→</div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
              {showCreateTeam && league && (
                <CreateTeamModal
                  leagueId={league.id} leagueSport={league.sport} leagueSeasonYear={league.season_year}
                  onClose={() => setShowCreateTeam(false)}
                  onCreated={() => { setShowCreateTeam(false); loadLeagueData(); }}
                />
              )}
            </div>
          )}

          {/* ── Schedule tab ── */}
          {tab === 'schedule' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <button className="btn btn-primary" onClick={() => setShowCreateGame(true)} disabled={teams.length === 0}>+ Schedule Game</button>
              </div>
              {games.length === 0 ? (
                <div style={{ ...card, textAlign: 'center', padding: '52px 20px' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>📅</div>
                  <p style={{ color: 'var(--ink-3)', marginBottom: '16px' }}>No games scheduled yet.</p>
                  <button className="btn btn-primary" onClick={() => setShowCreateGame(true)} disabled={teams.length === 0}>+ Schedule First Game</button>
                </div>
              ) : (
                <>
                  {upcoming.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>Upcoming ({upcoming.length})</div>
                      {upcoming.map(renderGame)}
                    </div>
                  )}
                  {past.length > 0 && (
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 800, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>Past Games ({past.length})</div>
                      {past.map(renderGame)}
                    </div>
                  )}
                </>
              )}
              {showCreateGame && league && (
                <CreateGameModal
                  leagueId={league.id} teams={teams}
                  onClose={() => setShowCreateGame(false)}
                  onCreated={() => { setShowCreateGame(false); loadLeagueData(); }}
                />
              )}
            </div>
          )}

          {/* ── Standings tab ── */}
          {tab === 'standings' && (
            <div style={card}>
              {standings.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--ink-4)' }}>
                  No completed games yet. Standings will appear once games are marked complete.
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--hairline)' }}>
                      {['#', 'Team', 'GP', 'W', 'L', 'D', 'GF', 'GA', 'GD', 'Pts'].map(h => (
                        <th key={h} style={{ padding: '10px 8px', textAlign: h === 'Team' ? 'left' : 'center', fontSize: '10px', fontWeight: 800, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '0.7px' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {standings.map((row, i) => (
                      <tr key={row.team.id} style={{ borderBottom: '1px solid var(--hairline)' }}>
                        <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 700, color: 'var(--ink-4)', fontSize: '12px' }}>{i + 1}</td>
                        <td style={{ padding: '10px 8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: row.team.primary_color, flexShrink: 0 }} />
                            <Link to={`/teams/${row.team.id}`} style={{ fontWeight: 700, color: 'var(--ink-1)', textDecoration: 'none' }}>{row.team.name}</Link>
                          </div>
                        </td>
                        {[row.gp, row.w, row.l, row.d, row.gf, row.ga, row.gf - row.ga].map((v, j) => (
                          <td key={j} style={{ padding: '10px 8px', textAlign: 'center', color: 'var(--ink-3)' }}>{v >= 0 ? (v > 0 && j === 6 ? `+${v}` : v) : v}</td>
                        ))}
                        <td style={{ padding: '10px 8px', textAlign: 'center', fontWeight: 900, fontSize: '15px', color: league.primary_color }}>{row.pts}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ── Settings tab ── */}
          {tab === 'settings' && (
            <div style={{ maxWidth: 560 }}>
              <div style={card}>
                <div style={{ fontSize: '14px', fontWeight: 800, color: 'var(--ink-1)', marginBottom: '16px' }}>League Information</div>
                <div style={{ marginBottom: '14px' }}><div style={lbl}>League Name</div><input style={inp} value={settingsForm.name || ''} onChange={e => setSettingsForm(f => ({ ...f, name: e.target.value }))} /></div>
                <div style={{ marginBottom: '14px' }}><div style={lbl}>Description</div><textarea style={{ ...inp, minHeight: '64px', resize: 'vertical' } as React.CSSProperties} value={settingsForm.description || ''} onChange={e => setSettingsForm(f => ({ ...f, description: e.target.value }))} /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                  <div><div style={lbl}>Location</div><input style={inp} value={settingsForm.location || ''} onChange={e => setSettingsForm(f => ({ ...f, location: e.target.value }))} /></div>
                  <div><div style={lbl}>Website</div><input style={inp} value={settingsForm.website_url || ''} onChange={e => setSettingsForm(f => ({ ...f, website_url: e.target.value }))} /></div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                  <div><div style={lbl}>Contact Name</div><input style={inp} value={settingsForm.contact_name || ''} onChange={e => setSettingsForm(f => ({ ...f, contact_name: e.target.value }))} /></div>
                  <div><div style={lbl}>Contact Email</div><input style={inp} value={settingsForm.contact_email || ''} onChange={e => setSettingsForm(f => ({ ...f, contact_email: e.target.value }))} /></div>
                </div>

                <div style={{ marginBottom: '14px' }}>
                  <div style={lbl}>Logo</div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    {settingsForm.logo_url && (
                      <div style={{ width: 56, height: 56, borderRadius: '10px', overflow: 'hidden', background: 'var(--surface-sunken)', border: '1px solid var(--hairline)', flexShrink: 0 }}>
                        <img src={mediaUrl(settingsForm.logo_url)} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      </div>
                    )}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <input style={inp} value={settingsForm.logo_url || ''} onChange={e => setSettingsForm(f => ({ ...f, logo_url: e.target.value }))} placeholder="Logo URL" />
                      <input ref={logoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { if (e.target.files?.[0]) uploadLogo(e.target.files[0]); }} />
                      <button type="button" onClick={() => logoRef.current?.click()} style={{ padding: '6px 12px', fontSize: '12px', fontWeight: 700, borderRadius: '7px', border: '1px solid var(--hairline)', background: 'var(--surface-muted)', color: 'var(--ink-3)', cursor: 'pointer' }}>⬆ Upload Logo</button>
                    </div>
                  </div>
                </div>

                {settingsMsg && (
                  <div style={{ marginBottom: '12px', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, background: settingsMsg === 'Saved!' ? 'rgba(34,197,94,0.1)' : 'rgba(248,113,113,0.1)', border: `1px solid ${settingsMsg === 'Saved!' ? 'rgba(34,197,94,0.3)' : 'rgba(248,113,113,0.25)'}`, color: settingsMsg === 'Saved!' ? '#22C55E' : '#F87171' }}>
                    {settingsMsg}
                  </div>
                )}
                <button className="btn btn-primary" onClick={saveSettings} disabled={settingsSaving}>{settingsSaving ? 'Saving…' : 'Save Changes'}</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
